import { AccessTime, LocationOn } from "@mui/icons-material";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import Lottie from "react-lottie";
import calendarAnimation from "../../../../../../assets/animations/calendar-animation-check.json";
import { selectCurrentCompany } from "../../../../../../redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "../../../../../../redux/hooks/redux";
import { formatDate } from "../../../../../../utils";
import { colors, alphaVar } from "../../../../../ui/colors";

export function JobScheduled({ data }) {
  const quote = data?.quote || {};
  const company = quote?.company || quote?.company?.client;
  const client = data?.events?.[0]?.client || data?.client || {};
  const coverageArea = data?.events?.[0]?.coverageArea || data?.coverageArea || {};
  const workType = data?.workScope?.workType?.name || "Service";

  const currentCompany = useAppSelector(selectCurrentCompany);
  const companyTimeZone = currentCompany?.timeZone || "UTC";

  const startDate = data?.events?.[0]?.startDate || quote?.startDate;
  const endDate = data?.events?.[0]?.endDate || quote?.endDate;

  const address = coverageArea?.address || coverageArea?.name || "No address specified";
  const city = coverageArea?.city || "";
  const state = coverageArea?.state || "";

  const squareFt = quote?.workScopeServices?.[0]?.squareFt || data?.workScope?.quotes?.[0]?.workScopeServices?.[0]?.squareFt || null;

  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: calendarAnimation,
    rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
  };

  return (
    <Box
      sx={{
        width: "100%",
        p: { xs: 2, md: 3 },
        bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
        position: "relative",
      }}
    >
      <Box
        sx={{
          px: { xs: 2.5, md: 4 },
          py: { xs: 2.5, md: 3 },
          borderRadius: "16px",
          bgcolor: "background.paper",
          border: `1px solid ${alphaVar(colors.gray[100], 0.55)}`,
          boxShadow: "none",
        }}
      >
      <Box textAlign="center" mb={3.5}>
        <Lottie options={defaultOptions} height={96} width={96} />
        <Typography
          variant="h4"
          fontWeight={600}
          color={colors.success.base}
          mt={1}
          sx={{
            fontSize: { xs: "1.2rem", sm: "1.65rem" },
          }}
        >
          Job Scheduled Successfully
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          mt={0.5}
          sx={{
            fontSize: { xs: "0.8rem", sm: "0.95rem" },
          }}
        >
          Your job has been scheduled for {formatDate(startDate, companyTimeZone, "MMMM d, yyyy")}
        </Typography>
      </Box>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2.5}>
        <Box flex={1}>
          <Stack direction="row" alignItems="center" spacing={0.75} mb={1.5}>
            <Box
              sx={{
                width: 4,
                height: 18,
                bgcolor: colors.orange.base,
                borderRadius: 1,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: colors.gray[800],
                fontWeight: 600,
                letterSpacing: 0.2,
                fontSize: "0.8rem",
              }}
            >
              Service Details
            </Typography>
          </Stack>

          <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: colors.gray[500], mb: 1 }}>
            {workType}
          </Typography>

          {squareFt && (
            <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", mb: 2 }}>
              Area: <strong>{squareFt.toLocaleString()} sq ft</strong>
            </Typography>
          )}

          <Stack spacing={1.75}>
            <Stack direction="row" spacing={1.25}>
              <AccessTime sx={{ color: colors.orange.base, fontSize: 16, mt: 0.15 }} />
              <Box>
                <Typography sx={{ fontSize: "0.76rem", fontWeight: 600, lineHeight: 1.35 }}>
                  {formatDate(startDate, companyTimeZone, "cccc, MMMM d, yyyy")}
                </Typography>
                <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", lineHeight: 1.4 }}>
                  {formatDate(startDate, companyTimeZone, "h:mm a")} - {formatDate(endDate, companyTimeZone, "h:mm a")}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.25}>
              <LocationOn sx={{ color: colors.orange.base, fontSize: 16, mt: 0.15 }} />
              <Box>
                <Typography sx={{ fontSize: "0.76rem", fontWeight: 600, lineHeight: 1.35 }}>
                  {address}
                </Typography>
                {(city || state) && (
                  <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", lineHeight: 1.4 }}>
                    {city}
                    {city && state ? ", " : ""}
                    {state}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Stack>
        </Box>

        <Box flex={1}>
          <Stack direction="row" alignItems="center" spacing={0.75} mb={1.5}>
            <Box
              sx={{
                width: 4,
                height: 18,
                bgcolor: colors.orange.base,
                borderRadius: 1,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: colors.gray[800],
                fontWeight: 600,
                letterSpacing: 0.2,
                fontSize: "0.8rem",
              }}
            >
              Contact Information
            </Typography>
          </Stack>

          <Stack spacing={2}>
            <Box>
              <Typography sx={{ fontSize: "0.62rem", color: "text.secondary", fontWeight: 600, mb: 0.75, display: "block" }}>
                CLIENT
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Avatar
                  src={client.avatar}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: colors.orange.base,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                  }}
                >
                  {(client.firstName?.[0] || "?").toUpperCase()}
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: "0.76rem", fontWeight: 600, lineHeight: 1.35 }}>
                    {client.firstName} {client.lastName}
                  </Typography>
                  {client.clientCompanyName && (
                    <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", lineHeight: 1.4 }}>
                      {client.clientCompanyName}
                    </Typography>
                  )}
                  {client.phone && (
                    <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", fontWeight: 500, lineHeight: 1.4 }}>
                      {client.phone}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Stack>
      </Box>
    </Box>
  );
}
